import React, { useMemo, ReactNode, useRef, useCallback, useState, useEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import MobileCardRow from './MobileCardRow';

export interface SortOrderItem {
  field: string;
  direction: 'asc' | 'desc';
}

// @ts-expect-error - T is used for type consistency in VirtualizedSpendTableProps
export interface SpendTableColumn<T = unknown> {
  key: string;
  label: string;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
}

export interface VirtualizedSpendTableProps<T> {
  title: string;
  data: T[]; // Data rows only - input row should NOT be included
  columns: SpendTableColumn<T>[];
  isCollapsed?: boolean;
  showTotals?: boolean;
  className?: string;
  sortOrder: SortOrderItem[];
  onSortOrderChange: (order: SortOrderItem[]) => void;
  getTopItems?: (data: T[], count: number) => T[];
  totalsRow?: T; // The Total row, always rendered last
  renderCell?: (row: T, columnKey: string, rowIdx?: number, colIdx?: number) => ReactNode;
  controlsAbove?: ReactNode;
  // Virtualization props
  rowHeight?: number;
  overscan?: number;
  containerHeight?: number;
  // Design variant
  variant?: 'default' | 'magazine';
}

// Type guard for input rows (id === -1)
function isInputRow<T extends { id?: string | number }>(row: T): boolean {
  return row.id === -1;
}

const VirtualizedSpendTable = <T extends { id?: string | number }>(props: VirtualizedSpendTableProps<T>) => {
  const {
    title,
    data,
    columns,
    isCollapsed = true,
    showTotals = true,
    className = '',
    sortOrder: _sortOrder, // Kept for interface compatibility but not used (header moved to LockedTableHeader)
    onSortOrderChange: _onSortOrderChange, // Kept for interface compatibility but not used (header moved to LockedTableHeader)
    getTopItems,
    totalsRow,
    renderCell,
    controlsAbove,
    rowHeight = 60,
    overscan = 5,
    containerHeight = 400,
    variant = 'default',
  } = props;

  // Mobile detection for responsive layout
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 640);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Helper class maps for the magazine style
  const isMag = variant === 'magazine';
  const tdClass = "theme-text-light";

  const rowBg = "table-row"; // ✅ FIX: Removed hover:bg-tableHover and transition-colors (user previously removed hover animations)

  const containerClass = isMag
    ? "magazine-table"
    : "table-shell overflow-hidden";

  const headerWrapClass = isMag
    ? "flex items-center px-6 pt-5 pb-1"
    : "flex items-center mb-1 px-6 py-4";

  const parentRef = useRef<HTMLDivElement>(null);

  // The data prop is now expected to be pre-sorted by the parent component.
  // The sortOrder prop is used only for rendering the sort icons.

  // Collapse logic - only apply when not virtualized (small datasets)
  // Data should NOT include input rows (id === -1) - they should be filtered out by parent
  const displayData = useMemo(() => {
    // Filter out any input rows that might have snuck in
    const filteredData = data.filter(row => !isInputRow(row));
    if (!isCollapsed || !getTopItems) return filteredData;
    // Only collapse if we have a small dataset
    if (filteredData.length <= 20) {
      return getTopItems(filteredData, 5);
    }
    return filteredData;
  }, [data, isCollapsed, getTopItems]);

  // Determine if we should virtualize
  const shouldVirtualize = displayData.length > 20;

  // Note: Sort handling moved to LockedTableHeader component
  // VirtualizedSpendTable now only handles data rows, not header

  // Virtualization setup
  const virtualizer = useVirtualizer({
    count: displayData.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan,
  });

  // Render virtualized row
  // Render virtualized row (magazine tweaks: softer row card effect, no stripes)
  const renderVirtualizedRow = useCallback((virtualRow: { size: number; start: number; index: number }, row: T, rowIdx: number) => {
    // Calculate column widths for proper alignment - match table column widths
    const getColumnWidth = (col: SpendTableColumn<T>) => {
      if (col.width) return col.width;
      // Default equal distribution if no width specified
      const totalColumns = columns.length;
      return `${100 / totalColumns}%`;
    };
    
    // Get alignment class based on column
    const getAlignClass = (col: SpendTableColumn<T>) => {
      if (col.align === 'left') return 'justify-start text-left';
      if (col.align === 'right') return 'justify-end text-right';
      return 'justify-center text-center'; // default center
    };
    
    return (
      <div
        key={row.id || `row-${rowIdx}`}
        style={{ 
          height: `${virtualRow.size}px`, 
          transform: `translateY(${virtualRow.start}px)`,
          transition: 'none' // ✅ FIX: Kill transitions to prevent levitation effect
        }}
        className={`absolute top-0 left-0 w-full ${rowBg}`}
      >
        <div 
          className={`flex ${variant === 'magazine' ? '' : 'theme-surface'} ${variant === 'magazine' && isInputRow(row) ? 'input-row theme-surface' : ''}`}
          style={{ transition: 'none' }} // ✅ FIX: Kill transitions on inner div too
        >
          {columns.map((column, colIdx) => {
            const colWidth = getColumnWidth(column);
            const alignClass = getAlignClass(column);
            return (
              <div
                key={column.key}
                className={`flex items-center ${alignClass} ${tdClass} ${variant === 'magazine' && column.key === 'actions' && isInputRow(row) ? 'actions-cell' : ''}`}
                style={{ width: colWidth, minWidth: colWidth, maxWidth: colWidth, flexShrink: 0, paddingTop: '22px', paddingBottom: '22px', paddingLeft: '24px', paddingRight: '24px' }}
              >
                {renderCell ? renderCell(row, column.key, rowIdx, colIdx) : (row as Record<string, unknown>)[column.key] as React.ReactNode}
              </div>
            );
          })}
        </div>
      </div>
    );
  }, [columns, renderCell, rowBg, tdClass, variant]);

  return (
    <div className={`relative w-full ${className}`}>
      {controlsAbove && <div className="flex justify-end items-center mb-4 gap-2">{controlsAbove}</div>}

      {/* === Container (borderless / magazine) === */}
      <div className={containerClass}>
        {title && (
          <div className={headerWrapClass}>
            <h3 className={`flex-1 ${isMag ? 'text-[17px] font-semibold theme-text-light' : 'text-lg font-semibold theme-text-light'}`}>{title}</h3>
          </div>
        )}
        
        {/* Mobile Card Layout */}
        {isMobile ? (
          <div className="space-y-3">
            {displayData.map((row, rowIdx) => (
              <MobileCardRow
                key={row.id || `mobile-row-${rowIdx}`}
                row={row}
                columns={columns}
                renderCell={renderCell}
                rowIdx={rowIdx}
                isTotalsRow={isInputRow(row)}
              />
            ))}
          </div>
        ) : (
          /* Desktop Table Layout - DATA ROWS ONLY (no header, no input row) */
          <div className={`virtualized-table ${variant === 'magazine' ? 'magazine-table' : ''} overflow-x-hidden`}>
            <table className="w-full theme-text-light table-fixed" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              {columns.map((column) => (
                <col key={column.key} style={{ width: column.width || 'auto' }} />
              ))}
            </colgroup>
            <tbody className={isMag ? 'divide-y divide-transparent' : ''}>
              {shouldVirtualize && containerHeight ? (
                <tr>
                  <td colSpan={columns.length} className="p-0">
                    <div ref={parentRef} className="relative overflow-y-auto overflow-x-hidden" style={{ height: `${containerHeight}px` }}>
                      <div style={{ height: `${virtualizer.getTotalSize()}px`, width: '100%', position: 'relative' }}>
                        {virtualizer.getVirtualItems().map((vr) => renderVirtualizedRow(vr, displayData[vr.index], vr.index))}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                <>
                  {displayData.map((row, rowIdx) => {
                    return (
                      <tr key={row.id || `row-${rowIdx}`} className={`${rowBg} ${variant === 'magazine' && isInputRow(row) ? 'input-row' : ''}`}>
                        {columns.map((column, colIdx) => {
                          const alignClass = column.align === 'left' ? 'text-left' : column.align === 'right' ? 'text-right' : 'text-center';
                          return (
                            <td 
                              key={column.key} 
                              className={`theme-text-light ${alignClass} ${variant === 'magazine' && column.key === 'actions' && isInputRow(row) ? 'actions-cell' : ''}`} 
                              style={{ width: column.width || 'auto', paddingTop: '22px', paddingBottom: '22px', paddingLeft: '24px', paddingRight: '24px' }}
                            >
                              {renderCell ? renderCell(row, column.key, rowIdx, colIdx) : (row as Record<string, unknown>)[column.key] as React.ReactNode}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </>
              )}
              
              {/* Totals Row – magazine footnote look */}
              {showTotals && totalsRow && (
                <tr className="totals-row">
                  {columns.map((column, colIdx) => {
                    const alignClass = column.align === 'left' ? 'text-left' : column.align === 'right' ? 'text-right' : 'text-center';
                    return (
                      <td
                        key={column.key}
                        className={`${isMag ? 'text-[13px] font-semibold theme-text-light' : 'text-sm font-semibold theme-text-light'} ${alignClass}`}
                        style={{ paddingTop: '22px', paddingBottom: '22px', paddingLeft: isMag ? '20px' : '24px', paddingRight: isMag ? '20px' : '24px' }}
                      >
                        {renderCell ? renderCell(totalsRow, column.key, displayData.length, colIdx) : ((totalsRow as Record<string, unknown>)[column.key] as React.ReactNode) || ''}
                      </td>
                    );
                  })}
                </tr>
              )}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(VirtualizedSpendTable) as <T extends { id?: string | number }>(
  props: VirtualizedSpendTableProps<T>
) => React.ReactElement;
